import { NgForm } from '@angular/forms';
import { AnessaTranslationService } from './helper/translation/anessa-translation.service';
import { UnitService } from './helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import Cookies from 'js-cookie';

import { Loan } from './models/project/financial/financial-investment/loan.model';
import { environment } from '../environments/environment';
import moment from 'moment';
import { LooseObject } from './models/loose-object.model';
import { ResultRow } from './models/data-structures/results/result-row.model';

class PasswordRule {
  valid = false;
  count = 0;

  constructor(
    public name: string,
    public required: number,
    public weight: number,
    public checkFunc: any
  ) {
  }
}

export default class Utils {

  static prepURL(url: string, baseHref: string) {
    if (baseHref === '/') {
      return url;
    } else {
      return baseHref + url;
    }
  }

  static isAccountAdmin() {
    return (localStorage.getItem('accountadmin') === 'true');
  }

  static isDBAdmin() {
    return (localStorage.getItem('dbadmin') === 'true');
  }

  static isInitialFormUser() {
    return (localStorage.getItem('userrole') === 'initialformuser');
  }

  static canViewHelp() {
    return (localStorage.getItem('canViewHelp') === 'true');
  }

  static getUserID() {
    return localStorage.getItem('userID');

  }

  /**
   * Checklist access for the Project Info page.
   *
   * Use {@link Utils.hasInputListAccess hasInputListAccess()} for checklist access on the Project Input page.
   * @returns true if the localstorage checklistAccess item is true
   */
  static hasChecklistAccess() {
    return (localStorage.getItem('checklistAccess') === 'true');
  }

  static canViewProjectInput() {
    return (localStorage.getItem('projectInputAccess') === 'true');
  }

  static isTranslator() {
    return (localStorage.getItem('isTranslator') === 'true');
  }

  static limitToScreeningProjects() {
    return (localStorage.getItem('limitToScreeningProjects') === 'true');
  }

  /**
   * Project Input checklist access
   *
   * Use {@link Utils.hasChecklistAccess hasChecklistAccess()} for access on the Project Info page.
   * @returns true if the localstorage inputListAccess item is true
   */
  static hasInputListAccess() {
    return (localStorage.getItem('inputListAccess') === 'true');
  }

  static hasFormAccess() {
    return (localStorage.getItem('screeningFormAccess') === 'true');
  }

  static hasFeature(feature: string) {
    return localStorage.getItem('features.' + feature) === 'true';
  }

  static isProjectLimitReached() {
    return (localStorage.getItem('reachedProjectLimit') === 'true');
  }

  static isScreeningProjectLimitReached() {
    return (localStorage.getItem('reachedScreeningProjectLimit') === 'true');
  }

  static canAccessADO() {
    return (localStorage.getItem('canAccessADO') === 'true');
  }

  static canAccessADM() {
    return (localStorage.getItem('canAccessADM') === 'true');
  }

  static canAccessADF() {
    return globalThis.location.hostname === "adademo.anessa.com" || globalThis.location.hostname === 'localhost';
  }

  static adoExpired(): boolean {
    return (localStorage.getItem('adoExpired') === 'true');
  }

  static admExpired(): boolean {
    return (localStorage.getItem('admExpired') === 'true');
  }

  /**
   * make an sso url:
   * <sso.com>/route?redirect={redirect}
   *
   * also adds &server={environment.name} if not on live or local
   */
  static makeSsoUrl(route: string, redirect: string) {
    let url = environment.ssoHubUrl + `/${route}?redirect=${redirect}`;
    url += !['account', 'local'].includes(environment.name) ? '&server=' + environment.name : '';
    return url;
  }

  static getLogoutUrl() {
    return this.makeSsoUrl('logout', 'ada');
  }

  static getAdoUrl() {
    return environment.platformUrls.ado;
  }

  static getAdmUrl() {
    return environment.platformUrls.adm;
  }

  static getAdfUrl() {
    return environment.platformUrls.adf;
  }

  static getAnessaTokenCookie() {
    return Cookies.get(`anessaTokenCookie-${environment.name}`);
  }

  static getExpiresAtCookie() {
    return Cookies.get(`anessaExpiresAt-${environment.name}`);
  }

  static getCookieDomain() {
    return environment.name === 'local' ? environment.hostname : 'anessa.com';
  }

  static setCookies(token: string, expiresAt: moment.Moment) {
    const maxAge = moment().subtract(expiresAt.valueOf(), 'milliseconds').valueOf() / -1000;
    const expiresAtMs = JSON.stringify(expiresAt.valueOf());
    const domain = this.getCookieDomain();
    let cookieCommon = `max-age=${maxAge};domain=${domain};path=/;secure;sameSite=strict`;
    if (environment.name ===  'local') {
      cookieCommon = `max-age=${maxAge};domain=${domain};path=/;sameSite=strict`;
    }
    document.cookie = `anessaTokenCookie-${environment.name}=${token};${cookieCommon}`;
    document.cookie = `anessaExpiresAt-${environment.name}=${expiresAtMs};${cookieCommon}`;
  }

  static createCookieThatExpiresInMinutes(name: string, data: string, minutes: number) {
    Cookies.set(name, data, {
      expires: new Date(new Date().getTime() + minutes * 60 * 1000),
      domain: this.getCookieDomain(),
      path: '/'
    });
  }

  static deepCopy<T>(obj: T): T {
    let copy: any;

    // Handle the 3 simple types, and null or undefined
    if (null == obj || 'object' !== typeof obj) {
      return obj;
    }

    // Handle Date
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (let i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.deepCopy(obj[i]);
      }
      return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
      // for custom class-instance-specific deepCopy implementations
      if (typeof (obj as LooseObject).deepCopy === 'function') {
        const cloneableObject: LooseObject = obj;
        return cloneableObject.deepCopy();
      }
      copy = {};
      for (const attr in obj) {
        if (obj.hasOwnProperty(attr)) {
          copy[attr] = this.deepCopy(obj[attr]);
        }
      }
      return copy;
    }

    throw new Error('unable to copy obj! Its type isn\'t supported.');
  }

  static isProjectLoaded() {
    if (localStorage.getItem('projectID') == null) {
      return false;
    }

    return localStorage.getItem('projectID') !== 'null';

  }

  static setProjectPermissions(canEdit: boolean, canSimulate: boolean, canDelete: boolean,
                               canEditInitialInformation: boolean, readOnlyInitialChecklists: boolean,
                               editOnlyInitialChecklists: boolean
  ) {
    localStorage.setItem('canEditProject', String(canEdit));
    localStorage.setItem('canSimulateProject', String(canSimulate));
    localStorage.setItem('canDeleteProject', String(canDelete));
    localStorage.setItem('canEditInitialInformation', String(canEditInitialInformation));
    if (this.isBoolean(readOnlyInitialChecklists)) {
      localStorage.setItem('readOnlyInitialChecklists', String(readOnlyInitialChecklists));
    } else {
      localStorage.setItem('readOnlyInitialChecklists', 'false');
    }
    if (this.isBoolean(editOnlyInitialChecklists)) {
      localStorage.setItem('editOnlyInitialChecklists', String(editOnlyInitialChecklists));
    } else {
      localStorage.setItem('editOnlyInitialChecklists', 'false');
    }
  }

  static clearProjectPermissions() {
    localStorage.removeItem('canEditProject');
    localStorage.removeItem('canSimulateProject');
    localStorage.removeItem('canDeleteProject');
    localStorage.removeItem('canEditInitialInformation');
    localStorage.removeItem('readOnlyInitialChecklists');
    localStorage.removeItem('editOnlyInitialChecklists');
  }

  static canEditProject() {
    if (localStorage.getItem('canEditProject') == null) {
      return false;
    }

    return localStorage.getItem('canEditProject') === 'true';
  }

  static canSimulateProject() {
    if (localStorage.getItem('canSimulateProject') == null) {
      return false;
    }

    return localStorage.getItem('canSimulateProject') === 'true';
  }

  static canDeleteProject() {
    if (localStorage.getItem('canDeleteProject') == null) {
      return false;
    }

    return localStorage.getItem('canDeleteProject') === 'true';
  }

  static canEditInitialInformation() {
    if (localStorage.getItem('canEditInitialInformation') == null) {
      return false;
    }

    return localStorage.getItem('canEditInitialInformation') === 'true';
  }

  static readOnlyInitialChecklists() {
    if (localStorage.getItem('readOnlyInitialChecklists') == null) {
      return false;
    }
    return localStorage.getItem('readOnlyInitialChecklists') === 'true';
  }

  static editOnlyInitialChecklists() {
    if (localStorage.getItem('editOnlyInitialChecklists') == null) {
      return false;
    }
    return localStorage.getItem('editOnlyInitialChecklists') === 'true';
  }

  static setLanguage(language: string) {
    localStorage.setItem('language', String(language));
  }

  static getAvailableLanguages() {
    const langs = localStorage.getItem('availableLanguages');
    return langs ? langs.split(',') : ['en'];
  }

  static getLanguage() {
    // Default to English if not set
    return localStorage.getItem('language') || 'en';
  }

  /**
   * Shows invalid controls on a form
   * Form controls must be required and have a name to be marked invalid
   * @param form used to find and touch invalid controls
   */
  static showInvalidControls(form: NgForm) {
    let controls;
    if (form.controls === undefined) {
      controls = form.control.controls;
    } else {
      controls = form.controls;
    }
    for (const name in controls) {
      if (controls[name].invalid) {
        controls[name].markAsTouched();
      }
    }
  }

  static camelcaseConverter(name: string) {
    const letters = name.split('');
    let result = '';
    for (let i = 0; i < letters.length; i++) {
      if (letters[i] === letters[i].toUpperCase()) {
        if (i + 1 < letters.length && letters[i + 1].toLowerCase() === letters[i + 1]) {
          if (i > 0) {
            result = result + ' ' + letters[i];
          } else {
            result = result + letters[i];
          }
        } else {
          if (i - 1 >= 0 && letters[i - 1].toLowerCase() === letters[i - 1]) {
            result = result + ' ' + letters[i];
          } else {
            result = result + letters[i];
          }
        }
      } else {
        result = result + letters[i];
      }
    }
    return result.toLowerCase();
  }

  static isSpecial(letter: string) {
    const specialCharacters = ' !"#$%&\'()*+,-./:;<=>?@[\]^_`{|}~';
    return specialCharacters.split('').includes(letter);
  }

  static getPasswordRequirements(translationService: AnessaTranslationService) {
    const requirements = [
      translationService.get('UTILS.PASS_REQ.REQ1'),
      translationService.get('UTILS.PASS_REQ.REQ2'),
      translationService.get('UTILS.PASS_REQ.REQ3'),
      translationService.get('UTILS.PASS_REQ.REQ4'),
      translationService.get('UTILS.PASS_REQ.REQ5')
    ];
    return translationService.get('UTILS.PASS_REQ.START') + '\n\n' + requirements.join('\n');
  }

  static checkPasswordStrength(password: string) {
    const rules = [
      new PasswordRule('length', 10, 40, function () {
        return true;
      }),
      new PasswordRule('lowercase', 1, 15, function (l: string) {
        return l === l.toLowerCase();
      }),
      new PasswordRule('uppercase', 1, 15, function (l: string) {
        return l === l.toUpperCase();
      }),
      new PasswordRule('number', 1, 15, function (l: any) {
        return !isNaN(l);
      }),
      new PasswordRule('special', 1, 15, this.isSpecial),
    ];

    password.split('').forEach(letter => {
      rules.forEach(rule => {
        if (rule.count !== rule.required && !rule.valid && rule.checkFunc(letter)) {
          rule.count++;
          rule.valid = rule.count === rule.required;
        }
      });
    });

    let percent = 0;
    rules.forEach(rule => {
      percent += (rule.count / rule.required) * (rule.weight / 100);
    });
    return percent;
  }

  static addCommas(value: string | number) {
    value = String(value);
    if (value != null && value.length > 0) {
      const conversion = Number(value.replace(/\,/gi, ''));
      if (!isNaN(conversion)) {
        // Remove any extraneous commas
        value = value.replace(/\,/gi, '');
        const numberComponents = value.split('.');
        const leftSide = numberComponents[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        numberComponents.splice(0, 1, leftSide);
        value = numberComponents.join('.');
      } else {
        value = '';
      }
    }
    return value;
  }

  static removeCommas(value: string | number) {
    value = String(value);
    if (value != null && value.length > 0) {
      value = value.replace(/\,/gi, '');
    }
    return Number(value);
  }

  /**
   * returns true or false depending on if the value is numeric or not
   * @param value is a string for which the result will be determined by
   */
  static isNumeric(value: any) {
    return value !== null && value !== '' && !isNaN(Number(value));
  }

  /**
   * return a true of false to signify if the provided parameter is a boolean or not
   * @param value will be checked if its type is boolean or not
   */
  static isBoolean(value: any) {
    return (typeof value === 'boolean');
  }

  static isString(value: any) {
    return (typeof value === 'string');
  }

  /**
   *  return boolean to signify if the provided param is a map. null and undefined are returns as false
   * @param map - type will be checked for instanceof Map
   */
  static isMap(map: any) {
    return map !== null && map !== undefined && map instanceof Map;
  }

  /**
   * returns true if the object is empty and false if it is not empty
   * @param obj object to check for any properties
   */
  static isEmpty(obj: any) {
    for (const prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        return false;
      }
    }
    return true;
  }

  /**
   * rounds the provided number based of the provided precision
   * @param value - the number that will be rounded
   * @param precision - the number of decimal places that will be rounded too; default precision is 2
   */
  static roundToPrecision(value: number, precision: number | null): number {
    if (!precision) return value;

    if (!this.isNumeric(value)) {
      return value;
    } else {
      value = Number(value);
    }
    if (!this.isNumeric(precision) || !value) { // if precision is not anything or value is zero
      return value;
    }
    if (Math.abs(value) < 1) {
      let exp = Math.ceil(Math.log10(1 / Math.abs(value))) + precision;
      if (precision >= 1) {
        exp -= 1;
      }
      return Math.round(value * 10 ** exp) / 10 ** exp;
    }
    // value large enough
    const multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
  }


  static roundDown(value: number, precision: number): number {
    if (!this.isNumeric(value)) {
      return value;
    } else {
      value = Number(value);
    }
    if (!this.isNumeric(precision) || !value) { // if precision is not anything or value is zero
      return value;
    }
    if (Math.abs(value) < 1) {
      let exp = Math.ceil(Math.log10(1 / Math.abs(value))) + precision;
      if (precision >= 1) {
        exp -= 1;
      }
      return Math.floor(value * 10 ** exp) / 10 ** exp;
    }
    // value large enough
    const multiplier = Math.pow(10, precision || 0);
    return Math.floor(value * multiplier) / multiplier;
  }


  static roundPrecisionUp(value: number, precision: number): number {
    if (!this.isNumeric(value)) {
      return value;
    } else {
      value = Number(value);
    }
    if (!this.isNumeric(precision) || !value) { // if precision is not anything or value is zero
      return value;
    }
    if (Math.abs(value) < 1) {
      let exp = Math.ceil(Math.log10(1 / Math.abs(value))) + precision;
      if (precision >= 1) {
        exp -= 1;
      }
      return Math.ceil(value * 10 ** exp) / 10 ** exp;
    }
    // value large enough
    const multiplier = Math.pow(10, precision || 0);
    return Math.ceil(value * multiplier) / multiplier;
  }

  static roundToPrecisionFixed(value: number, precision: number | null): string {
    if (!precision) return value + '';
    return this.roundToPrecision(value, precision).toFixed(precision);
  }

  /**
   * Rounds a number to the given precision and adds commas.
   * Shorthand for Utils.addCommas(Utils.roundToPrecision(value, precision))
   *
   * @param value the number that will be formatted
   * @param precision the number of decimal places that will be rounded to
   */
  static formatNumber(value: number, precision: number): string {
    return Utils.addCommas(Utils.roundToPrecision(value, precision));
  }

  /**
   * Creates an object containing the key "name" and value of the passed string
   * @param name string to be used in return object
   * @returns an object containing the key name and value of the passed name
   */
  static stringToNameObject(name: string): ({ name: string }) {
    return {
      name: name,
    };
  }

  /**
   * this function will check the provided name against the provided array of object for any matches.
   * if a match is found it will return false, otherwise it will return true. an ignore index can also
   * be set to exclude a certain element, this is used when editing an object as opposed to saving a new one.
   * this function will also throw an error if used improperly, such as one of the objects in the array provided
   * doesn't have a name.
   * @param name - the name that determine if a duplicate if present
   * @param obj - the array of objects that will be queried for a duplicate of the provided name
   * @param ignoreIndex - an optional parameter that can be used to ignore a certain index of the array of objects
   */
  static nameIsUnique(name: string, obj: any[], ignoreIndex: number | null = null) {
    if (obj && name) {
      for (const item of obj) {
        if (item['name'] && typeof (item['name']) === typeof (String())) {
          if (item['name'].toLowerCase().trim() === name.toLowerCase().trim()) {
            if (this.isNumeric(ignoreIndex)) {
              if (ignoreIndex !== obj.indexOf(item)) {
                return false;
              }
            } else {
              return false;
            }
          }
        }
      }
      return true;
    }
    throw Error('unable to validate name');
  }

  static checkIfStringExists(searchString: string, stringArray: string[], ignoreIndex: number | null = null) {
    if (stringArray && stringArray) {
      for (const item of stringArray) {
        if (item.toLowerCase() === searchString.toLowerCase()) {
          if (this.isNumeric(ignoreIndex)) {
            if (ignoreIndex !== stringArray.indexOf(item)) {
              return true;
            }
          } else {
            return true;
          }
        }
      }
      return false;
    }
  }

  /**
   * Truncate the provided string and postfix it will three dots if it's longer than the specified length.
   * @param name is the string that will be truncated.
   * @param maxLength is the max length of the string before it will be truncated.
   */
  static truncateString(name: string, maxLength: number) {
    if (maxLength < 3) {
      console.warn('name is to short to be truncated to maxLength');
      return '';
    }
    if (name.length <= maxLength) {
      return name;
    } else {
      return name.substr(0, (maxLength - 3)) + '...';
    }
  }

  /**
   * Processes the provided name string for translation potential.
   * @param name the name string that will be processed
   * @param translationService the translationService reference that will translate a translatable name
   * @param params optional params that can be used for translation
   */
  static processStringForTranslation(name: string, translationService: AnessaTranslationService, params = {}) {
    if (name && name.includes('.')) {
      return translationService.get(name, params);
    }
    return name;
  }

  static jsonStringifyMapReplacer(key: string, value: any) {
    if (value instanceof Map) {
      return {
        dataType: 'Map',
        value: Array.from([...value])
      };
    }
    return value;
  }

  static doesLookLikeUUID(id: string) {
    const uuidRegex = /\b[0-9a-f]{8}\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\b[0-9a-f]{12}\b/;
    return uuidRegex.test(id);
  }

  static restoreMapsInJson(obj: LooseObject, mapKey: string) {
    const updatedObj: LooseObject = {};
    for (const key of Object.keys(obj)) {
      if (key === mapKey) {
        updatedObj[key] = new Map(Object.entries(obj[key]));
      } else {
        if (obj[key]) {
          if (typeof obj[key] === 'object') {
            updatedObj[key] = this.restoreMapsInJson(obj[key], mapKey);
          } else {
            updatedObj[key] = obj[key];
          }
        }
      }
    }
    return updatedObj;
  }

  static isLoanAmountCapexBased(loan: Loan, unitService: UnitService) {
    const loanUnitType = unitService.getUnitById(loan.loanAmount.unit)?.unitType;
    if (loanUnitType === '000000000000000000000020') {
      // then it is currency type
      return false;
    } else if (loanUnitType === '000000000000000000000054') {
      // then it is percent type, meaning it is percent of capex type.
      return true;
    }
    return false;
  }

  static ObjectMapper(record: LooseObject, map: Function) {
    const entries = Object.entries(record);
    entries.forEach((entry) => {
      record[entry[0]] = map(entry[1]);
    });
    return record;
  }

  static toResultRows = (val: ResultRow) => new ResultRow(val);
}

